import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Coins, Minus, Plus, Save } from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ActivityRow, ReviewActions, useInvalidateAdmin } from '@/components/admin';
import { CheckRow } from '@/components/checks';
import { PurchaseRow, TransactionRow } from '@/components/lists';
import { AnimatedNumber, Page, Rise } from '@/components/motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, Input, Label } from '@/components/ui/input';
import { EmptyState, ErrorState, Skeleton, Switch, Tabs } from '@/components/ui/misc';
import { adminApi } from '@/lib/api/endpoints';
import { isApiError, userMessage } from '@/lib/api/errors';
import type { User } from '@/lib/api/types';
import { useMe } from '@/lib/auth';
import { formatDateTime, initials } from '@/lib/utils';
import { ListSkeleton } from '../ChecksPage';

type Tab = 'checks' | 'purchases' | 'ledger' | 'activity';

function AdjustCoinsDialog({ user, open, onOpenChange }: { user: User; open: boolean; onOpenChange: (v: boolean) => void }) {
  const [sign, setSign] = React.useState<1 | -1>(1);
  const [amount, setAmount] = React.useState('1');
  const [note, setNote] = React.useState('');
  const invalidate = useInvalidateAdmin();
  const n = Number(amount);
  const valid = Number.isInteger(n) && n > 0 && note.trim().length > 0;
  const m = useMutation({
    mutationFn: () => adminApi.adjustCoins(user.id, sign * n, note.trim()),
    onSuccess: (u) => {
      toast.success(`Balance is now ${u.coin_balance}`);
      invalidate();
      onOpenChange(false);
      setNote('');
    },
    onError: (e) => toast.error(isApiError(e, 409) ? `Can't remove more coins than @${user.username} has.` : userMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust coins</DialogTitle>
          <DialogDescription>@{user.username} has {user.coin_balance} coins. The note appears in their coin history.</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (valid) m.mutate(); }}>
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant={sign === 1 ? 'success' : 'outline'} onClick={() => setSign(1)}>
              <Plus /> Add
            </Button>
            <Button type="button" variant={sign === -1 ? 'destructive' : 'outline'} onClick={() => setSign(-1)}>
              <Minus /> Remove
            </Button>
          </div>
          <Field label="Coins" htmlFor="adj-amount">
            <Input id="adj-amount" type="number" min={1} icon={<Coins />} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Note (required)" htmlFor="adj-note">
            <Input id="adj-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. duplicate payment" />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" loading={m.isPending} disabled={!valid}>
              {sign === 1 ? 'Add' : 'Remove'} {Number.isInteger(n) && n > 0 ? n : ''} coins
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProfileCard({ user }: { user: User }) {
  const me = useMe();
  const self = me.id === user.id;
  const invalidate = useInvalidateAdmin();
  const [fullName, setFullName] = React.useState(user.full_name ?? '');
  const [phone, setPhone] = React.useState(user.phone ?? '');
  const [email, setEmail] = React.useState(user.email);
  React.useEffect(() => {
    setFullName(user.full_name ?? '');
    setPhone(user.phone ?? '');
    setEmail(user.email);
  }, [user.full_name, user.phone, user.email]);

  const update = useMutation({
    mutationFn: (body: Parameters<typeof adminApi.updateUser>[1]) => adminApi.updateUser(user.id, body),
    onSuccess: () => {
      toast.success('User updated');
      invalidate();
    },
    onError: (e) => toast.error(userMessage(e)),
  });
  const dirty = fullName !== (user.full_name ?? '') || phone !== (user.phone ?? '') || email !== user.email;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate({ full_name: fullName.trim() || null, phone: phone.trim() || null, email: email.trim().toLowerCase() });
          }}
        >
          <Field label="Full name" htmlFor="u-name">
            <Input id="u-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </Field>
          <Field label="Phone" htmlFor="u-phone">
            <Input id="u-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
          <Field label="Email" htmlFor="u-email">
            <Input id="u-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          {dirty && (
            <Button type="submit" size="sm" loading={update.isPending}>
              <Save /> Save changes
            </Button>
          )}
        </form>
        <div className="space-y-3 border-t pt-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="u-active">Active</Label>
              <p className="text-xs text-muted-foreground">Disabled users can't sign in.</p>
            </div>
            <Switch id="u-active" checked={user.is_active} disabled={self || update.isPending} onCheckedChange={(v) => update.mutate({ is_active: v })} />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="u-admin">Admin</Label>
              <p className="text-xs text-muted-foreground">Can review purchases and change rules.</p>
            </div>
            <Switch id="u-admin" checked={user.is_admin} disabled={self || update.isPending} onCheckedChange={(v) => update.mutate({ is_admin: v })} />
          </div>
          {self && <p className="text-xs text-muted-foreground">You can't disable or demote yourself.</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function UserTabs({ userId }: { userId: string }) {
  const [tab, setTab] = React.useState<Tab>('checks');
  const p = { user_id: userId, limit: 20 };
  const checks = useQuery({ queryKey: ['admin', 'checks', p], queryFn: () => adminApi.checks(p), enabled: tab === 'checks' });
  const purchases = useQuery({ queryKey: ['admin', 'purchases', p], queryFn: () => adminApi.purchases(p), enabled: tab === 'purchases' });
  const ledger = useQuery({ queryKey: ['admin', 'transactions', p], queryFn: () => adminApi.transactions(p), enabled: tab === 'ledger' });
  const activity = useQuery({ queryKey: ['admin', 'activity', p], queryFn: () => adminApi.activity(p), enabled: tab === 'activity' });
  const q = { checks, purchases, ledger, activity }[tab];
  const fullList = { checks: '/admin/checks', purchases: '/admin/purchases', ledger: '/admin/ledger', activity: '/admin/activity' }[tab];

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <Tabs
          layoutId="user-tabs"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'checks', label: 'Checks' },
            { value: 'purchases', label: 'Purchases' },
            { value: 'ledger', label: 'Ledger' },
            { value: 'activity', label: 'Activity' },
          ]}
        />
        <Button asChild variant="ghost" size="sm">
          <Link to={`${fullList}?user_id=${userId}`}>See all</Link>
        </Button>
      </div>
      <div className="border-t">
        {q.isPending ? (
          <ListSkeleton rows={3} />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : q.data.length === 0 ? (
          <EmptyState icon={<Coins />} title="Nothing here yet" />
        ) : (
          <div className="divide-y">
            {tab === 'checks' && checks.data?.map((c) => <CheckRow key={c.id} check={c} to={`/admin/checks/${c.id}`} />)}
            {tab === 'purchases' && purchases.data?.map((x) => <PurchaseRow key={x.id} purchase={x} actions={<ReviewActions purchase={x} />} />)}
            {tab === 'ledger' && ledger.data?.map((t) => <TransactionRow key={t.id} tx={t} showUser />)}
            {tab === 'activity' && activity.data?.map((a) => <ActivityRow key={a.id} a={a} />)}
          </div>
        )}
      </div>
    </Card>
  );
}

export function AdminUserDetailPage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['admin', 'user', id], queryFn: () => adminApi.user(id) });
  const [adjust, setAdjust] = React.useState(false);

  if (q.isPending) return <Skeleton className="h-80 w-full rounded-2xl" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const u = q.data;

  return (
    <Page className="space-y-6">
      <Rise className="pt-2">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/admin/users">
            <ArrowLeft /> Users
          </Link>
        </Button>
      </Rise>
      <Rise>
        <div className="relative overflow-hidden rounded-2xl border bg-card p-6 shadow-soft">
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-r from-brand-sun/25 via-amber-300/10 to-brand-navy/20" />
          <div className="relative flex flex-wrap items-end gap-5">
            <span className="flex size-20 items-center justify-center rounded-2xl bg-brand-navy text-2xl font-black text-brand-sun shadow-glow ring-4 ring-card">
              {initials(u.full_name || u.username)}
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="flex flex-wrap items-center gap-2 text-2xl font-extrabold tracking-tight">
                {u.full_name || u.username}
                {u.is_admin && <Badge tone="info">Admin</Badge>}
                {!u.is_active && <Badge tone="danger">Disabled</Badge>}
              </h1>
              <p className="text-sm text-muted-foreground">
                @{u.username} · joined {formatDateTime(u.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-4 rounded-xl border bg-background/60 px-4 py-2">
              <div>
                <p className="text-xs text-muted-foreground">Balance</p>
                <p className="flex items-center gap-1.5 text-2xl font-black">
                  <Coins className="size-5 text-amber-500" /> <AnimatedNumber value={u.coin_balance} />
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setAdjust(true)}>
                Adjust
              </Button>
            </div>
          </div>
        </div>
      </Rise>
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <Rise>
          <ProfileCard user={u} />
        </Rise>
        <Rise>
          <UserTabs userId={u.id} />
        </Rise>
      </div>
      <AdjustCoinsDialog user={u} open={adjust} onOpenChange={setAdjust} />
    </Page>
  );
}
